export interface LayoutConfiguration {
  enabled: boolean;
  greeting: {
    enabled: boolean;
    title: string;
    fallbackUserName: string;
  };
  supportTickets: {
    enabled: boolean;
    style: "list" | "carousel";
    statusesToDisplayOnThePage: string[];
    endUserTicketName: string;
    allowUserReplyToTicket: boolean;
  };
  askAiInput: {
    enabled: boolean;
    placeholder: string;
  };
}

interface ISDKSettings {
  tenantId: string;
  version?: "v1" | "v2";
  isActive: boolean;
  isTokenMasked: boolean;
  isNetworkBodyDisabled?: boolean;
  chat?: boolean;
  openChatDefaultList: string[];
  whiteListUrls: string[];
  proactiveListUrls: string[];
  proactiveTime: number;
  network?: boolean;
  console?: boolean;
  events?: boolean;
  pageViews?: boolean;
  recordings?: boolean;
  inject: boolean;
}

export type Theme = {
  iconPrimary: string;
  widgetPrimary: string;
  background: string;
  lightBorder: string;
  textPlaceholder: string;
  borderDefault: string;
  bgCard: string;
  bgInverted: string;
  bgPrimaryQuack: string;
  borderLight: string;
  textPrimary: string;
  textInverted: string;
  bgInput: string;
  userBackground: string;
  userText: string;
  agentBackground: string;
  agentText: string;
  bgChatWidget: string;
  bgSendButton?: string;
  textSendButton?: string;
  bgSecondaryButton?: string;
  textSecondaryButton?: string;
  borderSecondaryButton?: string;
  bgNotificationBadge?: string;
  fontFamily?: string;
};

const svgRadiuses = ["small", "medium", "large", "full"] as const;
export type SvgRadius = (typeof svgRadiuses)[number];

export const preChatAvatarRadiuses = ["regular", "rounded"] as const;
export type PreChatAvatarRadius = (typeof preChatAvatarRadiuses)[number];

export const minimizeIcons = ["chevron", "dash"] as const;
export type MinimizeIcon = (typeof minimizeIcons)[number];

export const csatTypes = ["rating", "binary"] as const;
export type CsatType = (typeof csatTypes)[number];

export type ChatSettings = {
  name: string;
  quackAgentName: string;
  theme: Theme;
  customSVG: string;
  closeSVG?: string;
  avatarSVG?: string;
  userAvatarSVG?: string;
  svgRadius?: SvgRadius;
  borderRadius: number;
  iconSize: number;
  position?: "bottom-left" | "bottom-right";
  iconPadding: number;
  poweredByLine?: boolean;
  firstMessage?: string;
  openFullyOnOpenChat?: boolean;
  disappearAfterClose?: number;
  fontSize?: number;
  waitTimeBeforeRefreshAfterEscalating?: number;
  escalationMessageToLiveAgent: string;
  escalationMessageToNotLiveAgent: string;
  placeholder?: string;
  fallbackSupportEmail?: string;
  csatDisabled?: boolean;
  csatType: CsatType;
  allowCsatComment?: boolean;
  csatCommentText?: string;
  allowUploadFileBeforeEscalation?: boolean;
  errorFallbackContent?: string;
  abandonedChatQuestion?: {
    waitTime: number;
    question: string;
  };
  allowNewChat?: boolean;
  allowCloseChat?: boolean;
  layout?: LayoutConfiguration;
  showUserBackground?: boolean;
  showCloseChatConfirmation?: boolean;
  helpCenterLink?: string;
  enableNewMessageSound?: boolean;
  enablePreChatFlow?: boolean;
  preChatWelcomeMessage?: string;
  preChatWelcomeSubtitle?: string;
  preChatCaseTitle?: string;
  preChatAvatarSVG?: string;
  preChatAvatarRadius?: PreChatAvatarRadius;
  minimizeIcon?: MinimizeIcon;
};

export type QuackChatConfig = ISDKSettings & {
  chatSettings: ChatSettings;
};

export type FeatureConfiguration = {
  isToggleOn: boolean;
  emails: string[];
};

export type QuackChatConfigResponse = ISDKSettings & {
  chatSettings: Omit<
    ChatSettings,
    | "quackAgentName"
    | "theme"
    | "customSVG"
    | "borderRadius"
    | "iconSize"
    | "iconPadding"
    | "escalationMessageToLiveAgent"
    | "escalationMessageToNotLiveAgent"
    | "csatType"
  > & {
    quackAgentName?: string;
    theme?: Theme;
    customSVG?: string;
    borderRadius?: number;
    iconSize?: number;
    iconPadding?: number;
    escalationMessageToLiveAgent?: string;
    escalationMessageToNotLiveAgent?: string;
    csatType?: CsatType;
  };
  contextAwareQuestionsConfiguration?: FeatureConfiguration;
  contextArrowsHelperConfiguration?: FeatureConfiguration;
};

export type CopilotConfigResponse = {
  id: string;
  origin: string;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  goal: string;
  isEnabled: boolean;
  isAskAnythingEnabled: boolean;
  sources: string[];
  copilotBlocks: Array<{
    id: string;
    type: string;
    order: number;
    isEnabled: boolean;
    configuration?: Record<string, any>;
  }>;
};
